'use client';

import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { ThemeProvider } from '@/lib/theme-context';
import { OrganizationProvider } from '@/contexts/organization-context';
import { ImpersonationProvider } from '@/contexts/impersonation-context';
import { ImpersonationBanner } from '@/components/impersonation-banner';
import { ConfirmDialogProvider } from '@/components/ui/confirm-dialog';
import { Toaster } from '@/components/ui/sonner';
import { ProductAnalyticsEvents } from '@/components/product-analytics';
import { useState, type ReactNode } from 'react';
import { usePathname } from 'next/navigation';

export function Providers({ children }: { children: ReactNode }) {
  const pathname = usePathname();
  // Public articles render before hydration; dashboard providers are not needed here.
  if (pathname === '/blog' || pathname?.startsWith('/blog/')) return <>{children}</>;
  return <ApplicationProviders>{children}</ApplicationProviders>;
}

function ApplicationProviders({ children }: { children: ReactNode }) {
  const [queryClient] = useState(
    () =>
      new QueryClient({
        defaultOptions: {
          queries: {
            staleTime: 1000 * 60 * 5, // Data stays fresh for 5 minutes
            cacheTime: 1000 * 60 * 10, // Cache kept for 10 minutes
            refetchOnWindowFocus: false, // Don't refetch when tab regains focus
            refetchOnMount: false, // Don't refetch when component remounts
            retry: 1, // Only retry failed requests once
          },
        },
      })
  );

  const appContent = <ConfirmDialogProvider>{children}</ConfirmDialogProvider>;

  return (
    <QueryClientProvider client={queryClient}>
      <ImpersonationProvider>
        <ImpersonationBanner />
        <OrganizationProvider>
          <ThemeProvider>
            <ProductAnalyticsEvents />
            {appContent}
            <Toaster richColors position="top-right" />
          </ThemeProvider>
        </OrganizationProvider>
      </ImpersonationProvider>
    </QueryClientProvider>
  );
}
